import { TextFading, useIsMobile } from '@yes.technology/react-toolkit'
import { useTranslation } from 'react-i18n-lite'

import { concatenateDisplayText } from 'shared/utils/object/concatenateFields'
import {
  ACTIONS_COLUMN_MAX_WIDTH,
  SELECTION_COLUMN_MAX_WIDTH
} from '../constants'
import {
  Badge,
  DataTableSkeletonDots,
  SortButton,
  TableHeaderColumn
} from '../DataTable.styles'
import {
  calculateNextSortDirection,
  createSortOptions,
  getColumnSortInfo
} from '../helpers/sorting'
import useTableContext from '../hooks/useTableContext'
import {
  ArrowsUpAndDown,
  AscendingSortArrow,
  DescendingSortArrow
} from '../SVG'

type TableHeaderProps = {
  isLoading: boolean
}

function TableHeader({ isLoading }: TableHeaderProps) {
  const { t } = useTranslation()

  const {
    mode,
    fields,
    actionColumns,
    actionColumnsPosition,
    columnsNames,
    columnsLayouts,
    sortOptions,
    onSort,
    informational,
    actionButton,
    embedded,
    hideRowActionButtons,
    hideSortButtons,
    concatFields,
    customActionButtons,
    insertOnly,
    textFading
  } = useTableContext()

  const isMobile = useIsMobile()

  const renderSortIcon = (
    direction: string,
    shouldShowBadge: boolean,
    sortOrderNumber: number | null
  ) => {
    return (
      <>
        {shouldShowBadge && <Badge>{sortOrderNumber}</Badge>}
        {direction === 'asc' && <AscendingSortArrow />}
        {direction === 'desc' && <DescendingSortArrow />}
        {direction === '' && <ArrowsUpAndDown />}
      </>
    )
  }

  const renderContentCell = (columnName: string) => {
    const { currentDirection, sortOrderNumber, shouldShowBadge } =
      getColumnSortInfo(sortOptions, columnName)
    const columnLayout = columnsLayouts.find(
      (layout) => layout.name === columnName
    )
    const columnWidth = columnLayout?.width

    if (isLoading) {
      return (
        <TableHeaderColumn key={columnName} width={columnWidth} mode={mode}>
          <DataTableSkeletonDots />
        </TableHeaderColumn>
      )
    }

    const columnDisplayText = concatenateDisplayText({
      concatFields,
      fields,
      columnName
    })

    const canShowSortButtons =
      mode === 'view' &&
      fields?.[columnName]?.type !== 'RELATION' &&
      !hideSortButtons

    const shouldEnableHeaderTextFading =
      columnLayout?.text_fading !== undefined
        ? columnLayout.text_fading
        : textFading

    const handleSortClick = (event: React.MouseEvent) => {
      const isMultipleSort = event.ctrlKey || event.metaKey

      const nextDirection = calculateNextSortDirection(currentDirection)

      const newSortOptions = createSortOptions(
        sortOptions,
        columnName,
        nextDirection,
        isMultipleSort
      )

      onSort(newSortOptions, false)
    }

    return (
      <TableHeaderColumn key={columnName} width={columnWidth} mode={mode}>
        {canShowSortButtons && (
          <SortButton
            role='button'
            onClick={handleSortClick}
            data-testid={`sort-${columnName}-${currentDirection}`}
          >
            {renderSortIcon(currentDirection, shouldShowBadge, sortOrderNumber)}
          </SortButton>
        )}
        {shouldEnableHeaderTextFading ? (
          <TextFading>{columnDisplayText}</TextFading>
        ) : (
          columnDisplayText
        )}
      </TableHeaderColumn>
    )
  }

  const renderActionCells = () => {
    const shouldShowActionColumn =
      !embedded ||
      actionButton?.type === 'document' ||
      !informational ||
      !!customActionButtons

    if (hideRowActionButtons === true || !shouldShowActionColumn) {
      return []
    }

    const className = `last fixed ${mode === 'view' ? 'text-center' : ''}`
    const style = isMobile
      ? { maxWidth: ACTIONS_COLUMN_MAX_WIDTH }
      : { width: ACTIONS_COLUMN_MAX_WIDTH, maxWidth: ACTIONS_COLUMN_MAX_WIDTH }

    return actionColumns.map((column) => (
      <TableHeaderColumn
        style={style}
        className={className}
        key={column.title}
        mode={mode}
      >
        {isLoading ? (
          <DataTableSkeletonDots />
        ) : (
          t(`data-tables.${column.title}`)
        )}
      </TableHeaderColumn>
    ))
  }

  const renderSelectCell = () => {
    if (informational || mode !== 'view') return null
    const style = isMobile
      ? { maxWidth: SELECTION_COLUMN_MAX_WIDTH }
      : {
          width: SELECTION_COLUMN_MAX_WIDTH,
          maxWidth: SELECTION_COLUMN_MAX_WIDTH
        }

    return (
      <TableHeaderColumn
        style={style}
        key='select-cell'
        mode={mode}
        className='first fixed'
      >
        {isLoading ? <DataTableSkeletonDots /> : ''}
      </TableHeaderColumn>
    )
  }

  if ((!fields || insertOnly) && mode !== 'insert') {
    return null
  }

  const contentCells = columnsNames.map(renderContentCell)
  const actionCells = renderActionCells()
  const selectCell = renderSelectCell()

  let headerCells: JSX.Element[]
  if (actionColumnsPosition === 'first') {
    headerCells = [selectCell, ...actionCells, ...contentCells].filter(
      Boolean
    ) as JSX.Element[]
  } else {
    headerCells = [selectCell, ...contentCells, ...actionCells].filter(
      Boolean
    ) as JSX.Element[]
  }

  return (
    <thead>
      <tr>{headerCells}</tr>
    </thead>
  )
}

export default TableHeader
